import { useMemo } from 'react';
import { StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import { useGetReleaseKitQuery } from '../../../../redux/api/fymAPI';
import { useRealtimePollInterval } from '../../../realtime/useRealtimePollInterval';
import { isAnyCardMaking, StudioCardWork, studioCardWork } from './studioCardWork';

/** The safety read of the song while something is being made and the realtime socket is down. */
export const STUDIO_CARD_WORK_POLL_MS = 30 * 1000;

const NO_WORK: ReadonlyMap<StudioThemeId, StudioCardWork> = new Map();

/**
 * The work of each asset type of the Studio song (step 2 cards). It reads the release kit answer
 * that the Studio already holds (`GET /release-kits/:catalogItemUuid`): no new query. The
 * `kit.asset.updated` and `reel.updated` events read it again while the socket is connected; a
 * second subscription reads it every 30 s only while something is being made and the socket is
 * down. "Make it" writes the new rows into the same cache entry (`fymAPI.ts`), so a card says
 * "1 making" at once.
 */
export function useStudioCardWork(
  accountId: number,
  catalogItemUuid: string
): ReadonlyMap<StudioThemeId, StudioCardWork> {
  const { data } = useGetReleaseKitQuery({ accountId, catalogItemUuid });
  const cards = useMemo(() => (data === undefined ? NO_WORK : studioCardWork(data.assets, data.reels)), [data]);
  const pollingInterval = useRealtimePollInterval(isAnyCardMaking(cards) ? STUDIO_CARD_WORK_POLL_MS : 0);
  useGetReleaseKitQuery({ accountId, catalogItemUuid }, { skip: pollingInterval === 0, pollingInterval });
  return cards;
}
